/**
 * The zone a viewer reads the lens in: their own by default, UTC on request,
 * kept per viewer in this browser. Shared with the playground, which keeps
 * its own choice under its own key. Storage may be missing or refuse (a
 * private window); the choice then lasts as long as the page.
 */

/** Where the console keeps the choice; the playground passes its own key. */
export const CONSOLE_ZONE_KEY = 'schedule-bit.console.zone';

/** The viewer's own zone, as the browser reports it; UTC when it says nothing. */
export const viewerZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

/** The zones a picker offers: the viewer's own first, then UTC, then every zone the browser knows. */
export const zoneOptions = (own: string): string[] => {
  let all: string[] = [];
  try {
    all = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  } catch {
    all = [];
  }
  return [...new Set([own, 'UTC', ...all])];
};

/** Whether the browser knows a zone by that name. */
export const knownZone = (zone: string): boolean => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
};

/** The zone this viewer chose before, or their own. */
export const loadZone = (storage: Pick<Storage, 'getItem'> | null = safeStorage(), key = CONSOLE_ZONE_KEY): string => {
  try {
    const saved = storage?.getItem(key);
    if (saved && knownZone(saved)) return saved;
  } catch {
    // storage refused: fall through to the viewer's own zone
  }
  return viewerZone();
};

export const saveZone = (zone: string, storage: Pick<Storage, 'setItem'> | null = safeStorage(), key = CONSOLE_ZONE_KEY): void => {
  try {
    storage?.setItem(key, zone);
  } catch {
    // storage refused: the choice lasts as long as the page
  }
};

const safeStorage = (): Storage | null => {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
};

/** "earlier · 00:00 UTC": a fix-it's words for one candidate of a repeated local time. */
export const occurrenceLabel = (c: { readonly occurrence: string; readonly instant: string }): string =>
  `${c.occurrence} · ${c.instant.slice(11, 16)} UTC`;
